// src/components/common/SearchSortControls.tsx
import React, { useRef, useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import StatusBadge from './StatusBadge';
import Section from '../common/Section';

export interface SortOption<K extends string = string> {
   key: K;
   label: string;
}

interface SearchSortControlsProps<K extends string> {
   search?: string;
   onSearchChange?: (value: string) => void;
   searchPlaceholder?: string;
   sortKey: K;
   sortAsc: boolean;
   onSortChange: (key: K) => void;
   sortOptions: SortOption<K>[];
   totalCount: number;
   filteredCount: number;
   /** Filter buttons — pass groups of <FilterGroup> or plain children */
   children?: React.ReactNode;
}

function SearchSortControls<K extends string>({
      search,
      onSearchChange,
      searchPlaceholder = 'Search…',
      sortKey,
      sortAsc,
      onSortChange,
      sortOptions,
      totalCount,
      filteredCount,
      children,
   }: SearchSortControlsProps<K>) {
   const [searchOpen, setSearchOpen] = useState(false);
   const inputRef = useRef<HTMLInputElement>(null);
   
   // Focus input when overlay opens
   useEffect(() => {
      if (searchOpen) inputRef.current?.focus();
   }, [searchOpen]);
   
   // Close overlay on Escape
   useEffect(() => {
      const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setSearchOpen(false); };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
   }, []);
   
   const hasSearch = search !== undefined && onSearchChange;
   const isFiltered = filteredCount !== totalCount;
   
   return (
      <Section className="relative">
         
         {/* ── Search overlay ───────────────────────────────────────────── */}
         {searchOpen && hasSearch && (
            <div className="absolute inset-0 z-10 bg-gray-900 rounded-xl flex items-center gap-2">
               <button onClick={() => setSearchOpen(false)}
                       className={`w-10 h-10 p-1.5 rounded-lg transition-colors shrink-0 ${
                          search ? 'text-blue-400 bg-blue-900/50 border-blue-700 border ring-1 ring-white/40 brightness-150' : 'text-gray-400 hover:text-white hover:bg-gray-800'
                       }`}
                       title="Search">
                  <Search className="w-4 h-4 mx-auto" />
               </button>
               <input
                  ref={inputRef}
                  value={search}
                  onChange={e => onSearchChange(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
               />
               {search && (
                  <button onClick={() => onSearchChange('')}
                          className="text-gray-500 hover:text-white transition-colors px-1">
                     <X className="w-3.5 h-3.5" />
                  </button>
               )}
               <button onClick={() => setSearchOpen(false)}
                       className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded transition-colors shrink-0">
                  Done
               </button>
            </div>
         )}
         
         {/* ── Main bar ────────────────────────────────────────────────── */}
         <div className="flex flex-wrap items-center gap-2">
            
            {/* Search icon button */}
            {hasSearch && (
               <button onClick={() => setSearchOpen(true)}
                       className={`w-10 h-10 p-1.5 rounded-lg transition-colors shrink-0 ${
                          search ? 'text-blue-400 bg-blue-900/50 border-blue-700 border ring-1 ring-white/40 brightness-150' : 'text-gray-400 hover:text-white hover:bg-gray-800'
                       }`}
                       title="Search">
                  <Search className="w-4 h-4 mx-auto" />
               </button>
            )}
            
            {/* Filter children (category, severity, etc.) */}
            {children}
            
            {/* Sort controls — pushed to the right */}
            <div className="flex items-center gap-1.5 ml-auto">
               <span className="text-xs text-gray-500 hidden sm:inline">Sort:</span>
               {sortOptions.map(opt => (
                  <StatusBadge
                     key={opt.key}
                     onClick={() => onSortChange(opt.key)}
                     active={sortKey === opt.key}
                     className={sortKey === opt.key
                        ? 'bg-blue-900/50 border-blue-700 text-blue-300'
                        : 'bg-gray-800 border-gray-700 text-gray-400'}
                  >
                     {opt.label}{sortKey === opt.key ? (sortAsc ? ' ↑' : ' ↓') : ''}
                  </StatusBadge>
               ))}
            </div>
            
            {/* Count */}
            <span className={`text-xs font-mono shrink-0 ${isFiltered ? 'text-blue-400' : 'text-gray-500'}`}>
               {filteredCount}{isFiltered ? ` / ${totalCount}` : ''}
            </span>
         
         </div>
      </Section>
   );
}

export default SearchSortControls;
